export const cloudflareGroups = [
  // ... (Tus grupos 1, 2 y 3 siguen igual) ...
  {
    title: "1. 🌐 DNS & Zone Management",
    description: "Core connectivity and network protocols.",
    fields: [
      { id: "cname_flattening", label: "CNAME Flattening", type: "select", options: ["flatten_at_root", "flatten_all", "none"], default: "flatten_at_root", recommended: "flatten_at_root", help: "Allows using a CNAME record at the root domain." },
      { id: "ipv6", label: "IPv6 Compatibility", type: "toggle", recommended: true, help: "Enables IPv6 support." },
      { id: "websockets", label: "WebSockets", type: "toggle", recommended: true, help: "Allows open WebSocket connections." },
      { id: "pseudo_ipv4", label: "Pseudo IPv4 Header", type: "toggle", recommended: false, help: "Adds a simulated IPv4 header." },
      { id: "ip_geolocation", label: "IP Geolocation", type: "toggle", recommended: true, help: "Includes visitor country code." },
      { id: "zero_rtt", label: "0-RTT Resumption", type: "toggle", recommended: true, help: "Improves performance for returning visitors." },
      { id: "http3", label: "HTTP/3 (QUIC)", type: "toggle", recommended: true, help: "Enables next-gen HTTP/3 protocol." }
    ]
  },
  {
    title: "2. 🔒 Security & SSL",
    description: "Strict encryption and threat protection.",
    fields: [
      { id: "ssl", label: "SSL Mode", type: "select", options: ["off", "flexible", "full", "strict"], default: "strict", recommended: "strict", help: "Defines encryption level. 'Strict' is recommended." },
      { id: "always_use_https", label: "Always Use HTTPS", type: "toggle", recommended: true, help: "Redirects http to https." },
      { id: "min_tls_version", label: "Min TLS Version", type: "select", options: ["1.0", "1.1", "1.2", "1.3"], default: "1.2", recommended: "1.2", help: "Minimum required TLS version." },
      { id: "security_level", label: "Security Level", type: "select", options: ["off", "essentially_off", "low", "medium", "high", "under_attack"], default: "medium", recommended: "medium", help: "Firewall sensitivity." },
      { id: "browser_check", label: "Browser Integrity Check", type: "toggle", recommended: true, help: "Blocks basic bad bots." },
      { id: "privacy_pass", label: "Privacy Pass", type: "toggle", recommended: true, help: "Reduces CAPTCHAs for legitimate users." },
      { id: "email_obfuscation", label: "Email Obfuscation", type: "toggle", recommended: true, help: "Hides emails from scrapers." },
      { id: "hotlink_protection", label: "Hotlink Protection", type: "toggle", recommended: false, help: "Prevents image theft." }
    ]
  },
  {
    title: "3. ⚡ Performance & Caching",
    description: "Speed optimization and caching strategies.",
    fields: [
      { id: "caching_level", label: "Caching Level", type: "select", options: ["No query string", "Ignore query string", "Standard"], default: "Standard", recommended: "Standard", help: "How aggressively to cache." },
      { id: "brotli", label: "Brotli Compression", type: "toggle", recommended: true, help: "Better compression than Gzip." },
      { id: "rocket_loader", label: "Rocket Loader™", type: "toggle", recommended: true, help: "Async JS loading." },
      { id: "early_hints", label: "Early Hints", type: "toggle", recommended: true, help: "Preload resources hint." },
      { id: "automatic_https_rewrites", label: "Auto HTTPS Rewrites", type: "toggle", recommended: true, help: "Fixes mixed content errors." },
      { id: "mirage", label: "Mirage (Pro)", type: "toggle", recommended: false, help: "Image optimization for mobile." },
      { id: "webp", label: "WebP (Pro)", type: "toggle", recommended: false, help: "Serve WebP images." },
      { id: "polish", label: "Polish (Pro)", type: "select", options: ["off", "lossy", "lossless"], default: "off", recommended: "off", help: "Auto image compression." }
      
      // --- CACHE RULES (Business+ only) ---
      // DISABLED: These options require Business+ plan and cause 403 errors on Free/Pro zones
      // If needed for Enterprise customers, uncomment and ensure zone plan is verified before enabling
      // { id: "cache_static_assets", label: "Cache Static Assets (Business+)", type: "toggle", recommended: false, help: "⚠️ Requires Business+ plan - Cache everything under /static/*, /assets/*, /images/*" },
      // { id: "cache_static_ttl", label: "Static Assets TTL", type: "select", options: ["1h", "6h", "1d", "7d", "30d"], default: "7d", recommended: "7d", help: "How long to cache static assets on edge (Business+ only)" },
      // { id: "cache_bypass_api", label: "Bypass Cache for API (Business+)", type: "toggle", recommended: false, help: "⚠️ Requires Business+ plan - Never cache /api/* endpoints" },
      // { id: "cache_by_device", label: "Cache by Device Type (Business+)", type: "toggle", recommended: false, help: "⚠️ Requires Business+ plan - Separate cache for mobile vs desktop" }
    ]
  },
  {
    title: "5. 🤖 Bot Management",
    isBotManagement: true,
    description: "Enterprise Bot Management onboarding. Requires Bot Management module enabled on the zone.",
    fields: [
      // --- Core toggle ---
      {
        id: "bot_ml_auto_update",
        label: "ML Model Auto-Updates",
        type: "toggle",
        recommended: true,
        help: "Keeps Cloudflare's bot detection model continuously updated. Enable via Security → Settings → Bot Management → Enable Auto-update machine learning model."
      },

      // --- Rule 1: Definite Bots ---
      {
        id: "bot_definite_enable",
        label: "Rule: Definite Bots (score = 1)",
        type: "toggle",
        recommended: true,
        help: "Activates a WAF rule for fully automated bots (Bot Score = 1). Excludes verified bots and static assets. Start in Log mode, then switch to Block after 24–48h validation."
      },
      {
        id: "bot_definite_name",
        label: "Definite Bots Rule Name",
        type: "text",
        default: "[Helix] Bot Management — Definite Bots",
        help: "Display name for the Definite Bots WAF rule."
      },
      {
        id: "bot_definite_expression",
        label: "Definite Bots Expression",
        type: "text",
        default: "(cf.bot_management.score eq 1 and not cf.bot_management.verified_bot and not cf.bot_management.static_resource)",
        help: "Cloudflare rule expression for definite bots. Matches score = 1, excluding verified bots and static resources."
      },
      {
        id: "bot_definite_action",
        label: "Definite Bots Action",
        type: "select",
        options: ["log", "block", "managed_challenge", "js_challenge"],
        default: "log",
        help: "Action for definite bots. Use 'log' first for baseline, then 'block' for production enforcement."
      },

      // --- Rule 2: Likely Bots ---
      {
        id: "bot_likely_enable",
        label: "Rule: Likely Bots (score 2–29)",
        type: "toggle",
        recommended: true,
        help: "Activates a WAF rule for likely automated traffic (Bot Score 2–29). Excludes verified bots and static assets. Start in Log mode, then switch to Managed Challenge."
      },
      {
        id: "bot_likely_name",
        label: "Likely Bots Rule Name",
        type: "text",
        default: "[Helix] Bot Management — Likely Bots",
        help: "Display name for the Likely Bots WAF rule."
      },
      {
        id: "bot_likely_expression",
        label: "Likely Bots Expression",
        type: "text",
        default: "(cf.bot_management.score ge 2 and cf.bot_management.score le 29 and not cf.bot_management.verified_bot and not cf.bot_management.static_resource)",
        help: "Cloudflare rule expression for likely bots. Matches scores 2–29, excluding verified bots and static resources."
      },
      {
        id: "bot_likely_action",
        label: "Likely Bots Action",
        type: "select",
        options: ["log", "managed_challenge", "js_challenge", "block"],
        default: "log",
        help: "Action for likely bots. Use 'log' first for baseline, then 'managed_challenge' for production enforcement."
      },

      // --- Rule 3: JS Detection ---
      {
        id: "bot_js_detection_enable",
        label: "Rule: JS Detection (clients failing JS)",
        type: "toggle",
        recommended: false,
        help: "Enables the JS Detection WAF rule AND automatically turns on JavaScript Detections at the zone level (Security → Settings → Bot Management → Enable JavaScript Detections). Required for cf.bot_management.js_detection.passed to be populated. Per TASE playbook Step 5."
      },
      {
        id: "bot_js_detection_name",
        label: "JS Detection Rule Name",
        type: "text",
        default: "[Helix] Bot Management — JS Detection",
        help: "Display name for the JavaScript Detection WAF rule."
      },
      {
        id: "bot_js_detection_expression",
        label: "JS Detection Expression",
        type: "text",
        default: '(not cf.bot_management.js_detection.passed and http.request.method eq "GET" and not cf.bot_management.verified_bot)',
        help: "Matches GET requests that fail JS detection, excluding verified bots. Customize the method/path as needed."
      },
      {
        id: "bot_js_detection_action",
        label: "JS Detection Action",
        type: "select",
        options: ["log", "js_challenge", "managed_challenge", "block"],
        default: "log",
        help: "Action for clients failing JS detection. Use 'log' first, then 'js_challenge' for enforcement."
      },

      // --- Rule 4: AI Bots ---
      {
        id: "bot_ai_block_enable",
        label: "Rule: AI Bot Traffic (OpenAI, Anthropic, etc.)",
        type: "toggle",
        recommended: false,
        help: "Optional. Blocks or challenges detected AI crawlers (OpenAI, Anthropic, Perplexity, etc.). Use if customer content is proprietary. Enable AI Labyrinth in Security → Settings for additional visibility."
      },
      {
        id: "bot_ai_block_name",
        label: "AI Bots Rule Name",
        type: "text",
        default: "[Helix] Bot Management — AI Bots",
        help: "Display name for the AI Bot WAF rule."
      },
      {
        id: "bot_ai_block_expression",
        label: "AI Bots Expression",
        type: "text",
        default: "(any(cf.bot_management.detection_ids[*] in {123815556 132995013 126255384 33563859 33564301 33564303 33563889 33564371 33563853 133621792 33563855 124581738 33563982 33563972 118601807 133730073 126666910 128950951}))",
        help: "Matches AI crawlers by detection ID (source: developers.cloudflare.com/ai-crawl-control/reference/bots). Covers GPTBot, ChatGPT-User, ClaudeBot, PerplexityBot, Bytespider, CCBot, Meta bots, Amazonbot, Google-CloudVertexBot, DuckAssistBot, MistralAI-User. Requires Enterprise Bot Management."
      },
      {
        id: "bot_ai_block_action",
        label: "AI Bots Action",
        type: "select",
        options: ["log", "block", "managed_challenge"],
        default: "log",
        help: "Action for AI bots. Use 'log' first to assess traffic volume, then 'block' or 'managed_challenge' for enforcement."
      }
    ]
  },
  {
    title: "4. 🛡️ WAF & Managed Rules",
    isWaf: true,
    description: "Custom rules and Cloudflare Managed Sets.",
    fields: [
      { id: "waf_enable", label: "Enable Custom Rule", type: "toggle", recommended: true, help: "Activates a custom firewall rule defined below." },
      { id: "waf_name", label: "Custom Rule Name", type: "text", default: "Helix Shield", help: "Descriptive name for your custom rule." },
      { id: "waf_expression", label: "Expression", type: "text", default: 'ip.geoip.country eq "CN"', help: "Logic for the custom rule (e.g., block China)." },
      
      // ✅ AQUÍ ESTÁ EL CAMBIO IMPORTANTE:
      { 
        id: "waf_action", 
        label: "Action", 
        type: "select", 
        // Estas opciones coinciden con tu captura (mapeadas a valores de API)
        options: ["managed_challenge", "log", "block", "js_challenge", "skip"], 
        default: "managed_challenge", 
        help: "Action to take when a request matches the custom rule." 
      },
      
      { 
        id: "waf_managed_cloudflare", 
        label: "Cloudflare Managed Ruleset", 
        type: "toggle", 
        recommended: true, 
        help: "Enables Cloudflare's default protection against common vulnerabilities and exploits." 
      },
      { 
        id: "waf_managed_owasp", 
        label: "OWASP Core Ruleset", 
        type: "toggle", 
        recommended: true, 
        help: "Enables protection against OWASP Top 10 vulnerabilities (Enterprise/Pro)." 
      }
    ]
  },
  {
    title: "6. 📡 Spectrum Applications",
    isSpectrum: true,
    description: "Cloudflare Spectrum — L3/L4 DDoS protection for non-HTTP protocols (SSH, RDP, SFTP, custom TCP/UDP). Requires Spectrum entitlement on account. Entitlements must be set in Ninja Panel before deploying.",
    fields: [
      // ── PREREQUISITE NOTE ──
      {
        id: "_spectrum_prereq_note",
        label: "⚠️ Prerequisites",
        type: "readonly",
        value: "Spectrum is NOT included in the Externa bundle — it is a separate paid add-on. Before deploying: (1) Confirm DDOS_PROTECTION_SPECTRUM_ENT subscription is contracted. (2) Enable Proxy Anything entitlement at both zone and account level in Ninja Panel → User → Entitlement. (3) For UDP or Argo Smart Routing, add Product Features via Ninja Panel → Product Features → proxyanything → scope account/zone. (4) Verify zone is Enterprise plan.",
        help: "Spectrum requires a separate contract subscription (DDOS_PROTECTION_SPECTRUM_ENT). It is not included in Externa Essentials or Advantage bundles. Entitlements must be set in Ninja Panel before any Spectrum API calls will succeed."
      },

      // ══════════════════════════════════════
      // APP 1
      // ══════════════════════════════════════
      {
        id: "spectrum_app1_enable",
        label: "App 1 — Enable",
        type: "toggle",
        recommended: true,
        help: "Enable Spectrum App 1. Typically the primary HTTPS (443) or SSH (22) app."
      },
      {
        id: "spectrum_app1_dns_name",
        label: "App 1 — Edge Hostname",
        type: "text",
        default: "",
        help: "The hostname clients connect to, e.g. ssh.example.com or myapp.example.com. Cloudflare auto-creates the DNS CNAME — no manual DNS entry needed."
      },
      {
        id: "spectrum_app1_protocol",
        label: "App 1 — Protocol / Port",
        type: "text",
        default: "tcp/443",
        help: "Format: tcp/PORT or udp/PORT or tcp/START-END. Examples: tcp/443, tcp/22, tcp/3389, udp/1194, tcp/1000-2000."
      },
      {
        id: "spectrum_app1_traffic_type",
        label: "App 1 — Traffic Type",
        type: "select",
        options: ["direct", "http", "https"],
        default: "direct",
        help: "'direct' = L4 TCP/UDP passthrough (required for SSH, RDP, SFTP). 'http'/'https' = routes through Cloudflare HTTP pipeline (enables WAF, Workers, Bot Management). Argo Smart Routing only works with 'direct'."
      },
      {
        id: "spectrum_app1_origin_type",
        label: "App 1 — Origin Type",
        type: "select",
        options: ["dns", "direct"],
        default: "dns",
        help: "'dns' = origin specified as a hostname (Enterprise only, recommended). 'direct' = origin specified as IP address(es)."
      },
      {
        id: "spectrum_app1_origin_dns_name",
        label: "App 1 — Origin Hostname",
        type: "text",
        default: "",
        help: "Hostname of the origin server, e.g. origin.example.com. Used when Origin Type = dns. Enterprise only."
      },
      {
        id: "spectrum_app1_origin_port",
        label: "App 1 — Origin Port",
        type: "text",
        default: "443",
        help: "Destination port at the origin server. Must match the protocol port or use a range matching the edge port range (e.g. 443, 22, 3000-4000)."
      },
      {
        id: "spectrum_app1_tls",
        label: "App 1 — TLS Mode",
        type: "select",
        options: ["off", "flexible", "full", "strict"],
        default: "full",
        help: "'off' = passthrough, origin handles TLS (use for SSH, RDP). 'flexible' = TLS at edge, plain to origin. 'full' = TLS at edge + encrypted to origin (no cert validation). 'strict' = TLS at edge + encrypted to origin with cert validation."
      },
      {
        id: "spectrum_app1_ip_firewall",
        label: "App 1 — IP Firewall",
        type: "toggle",
        recommended: true,
        help: "Enforces IP Access Rules (Security → WAF → Tools) for this Spectrum app. Only allow/block rules apply. Enterprise only."
      },
      {
        id: "spectrum_app1_proxy_protocol",
        label: "App 1 — Proxy Protocol",
        type: "select",
        options: ["off", "v1", "v2", "simple"],
        default: "off",
        help: "Passes real client IP to origin. 'off' = disabled. 'v1' = text header (TCP only, compatible with NGINX/ELB). 'v2' = binary header (TCP+UDP). 'simple' = Cloudflare UDP-only lightweight protocol."
      },
      {
        id: "spectrum_app1_argo",
        label: "App 1 — Argo Smart Routing",
        type: "toggle",
        recommended: true,
        help: "Routes traffic via the fastest Cloudflare network path. TCP + traffic_type=direct only. Requires Argo entitlement in Ninja Panel → Product Features."
      },
      {
        id: "spectrum_app1_edge_connectivity",
        label: "App 1 — Edge IP Connectivity",
        type: "select",
        options: ["all", "ipv4", "ipv6"],
        default: "all",
        help: "IP versions assigned to the edge hostname. 'all' = dual-stack IPv4+IPv6. 'ipv4' = IPv4 only. 'ipv6' = IPv6 only."
      },

      // ══════════════════════════════════════
      // APP 2 (HTTP companion / second protocol)
      // ══════════════════════════════════════
      {
        id: "spectrum_app2_enable",
        label: "App 2 — Enable",
        type: "toggle",
        recommended: false,
        help: "Enable a second Spectrum app (e.g. HTTP port 80 companion). Required if you need HTTP traffic — without it, port 80 returns an error. Also used for a second protocol on a different port."
      },
      {
        id: "spectrum_app2_dns_name",
        label: "App 2 — Edge Hostname",
        type: "text",
        default: "",
        help: "Hostname for App 2. For HTTP companion, use the same hostname as App 1."
      },
      {
        id: "spectrum_app2_protocol",
        label: "App 2 — Protocol / Port",
        type: "text",
        default: "tcp/80",
        help: "Protocol and port for App 2. Use tcp/80 for HTTP companion to the HTTPS App 1."
      },
      {
        id: "spectrum_app2_traffic_type",
        label: "App 2 — Traffic Type",
        type: "select",
        options: ["direct", "http", "https"],
        default: "direct",
        help: "Traffic routing mode for App 2."
      },
      {
        id: "spectrum_app2_origin_type",
        label: "App 2 — Origin Type",
        type: "select",
        options: ["dns", "direct"],
        default: "dns",
        help: "Origin type for App 2. Usually same as App 1."
      },
      {
        id: "spectrum_app2_origin_dns_name",
        label: "App 2 — Origin Hostname",
        type: "text",
        default: "",
        help: "Origin hostname for App 2."
      },
      {
        id: "spectrum_app2_origin_port",
        label: "App 2 — Origin Port",
        type: "text",
        default: "80",
        help: "Destination port at origin for App 2."
      },
      {
        id: "spectrum_app2_tls",
        label: "App 2 — TLS Mode",
        type: "select",
        options: ["off", "flexible", "full", "strict"],
        default: "off",
        help: "TLS mode for App 2. Use 'off' for HTTP port 80 companion."
      },
      {
        id: "spectrum_app2_ip_firewall",
        label: "App 2 — IP Firewall",
        type: "toggle",
        recommended: true,
        help: "IP Firewall for App 2."
      },
      {
        id: "spectrum_app2_proxy_protocol",
        label: "App 2 — Proxy Protocol",
        type: "select",
        options: ["off", "v1", "v2", "simple"],
        default: "off",
        help: "Proxy Protocol for App 2."
      },
      {
        id: "spectrum_app2_argo",
        label: "App 2 — Argo Smart Routing",
        type: "toggle",
        recommended: false,
        help: "Argo for App 2. Generally not needed for HTTP port 80 companion."
      },
      {
        id: "spectrum_app2_edge_connectivity",
        label: "App 2 — Edge IP Connectivity",
        type: "select",
        options: ["all", "ipv4", "ipv6"],
        default: "all",
        help: "Edge IP connectivity for App 2."
      }
    ]
  }
];
